//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import cx from 'classnames';
import React from 'react';
import { U } from 'ts-toolbelt';
import styled from 'styled-components';
import { UnisonIconType } from '@bvengineering/unison-core';
import { Badge as CuBadge } from 'unison';

import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import Icon from '@components/atoms/icon/Icon';

export enum BadgeType {
  Primary = 'primary',
  Secondary = 'secondary',
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
  NeedsAction = 'needs-action',
  Neutral = 'neutral',
  New = 'new',
  Focus = 'focus',
}

interface IOwnProps extends React.HTMLAttributes<HTMLDivElement> {
  type: BadgeType;
  size?: 'small' | 'default';
}

interface IIconBadgeProps extends IOwnProps {
  iconName: UnisonIconType;
}

interface ITextBadgeProps extends IOwnProps {
  text: string;
  tooltipContent?: string;
}

type Props = U.Strict<IIconBadgeProps | ITextBadgeProps>;

/**
 * The Badge atom renders a string of text or a single icon in
 * a badge using a standard background, border, and
 * color combination. The badge will have a tooltip if
 * tooltipContent is provided.
 */
export const Badge: React.FunctionComponent<Props> = ({
  className,
  iconName,
  size = 'default',
  text,
  type,
  tooltipContent,
}) => {
  const badgeClassnames = cx(className, `cu-badge-${type}`, {
    'cu-badge-icon': !!iconName,
    'cu-badge-small': size === 'small',
  });

  return (
    <SmallTooltip
      isDisabled={!tooltipContent}
      delay={{ showDelay: 500 }}
      target={
        <BadgeAtom className={badgeClassnames}>
          {iconName ? <Icon name={iconName} /> : text}
        </BadgeAtom>
      }
      content={tooltipContent}
    />
  );
};

const BadgeAtom = styled(CuBadge)`
  /** Ensures Badge atoms are always one line of text */
  white-space: nowrap;
  cursor: default;
`;

export const BetaBadge = (props: Omit<IOwnProps, 'text' | 'type'>) => (
  <Badge type={BadgeType.New} text='beta' {...props} />
);

export const NewBadge = (props: Omit<IOwnProps, 'text' | 'type'>) => (
  <Badge type={BadgeType.New} text='new' {...props} />
);

export default Badge;
